// Date formatting and validation utilities

export const formatDate = (date) => {
  if (!date || !(date instanceof Date)) return '';
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export const formatDateWithWeekday = (dateString) => {
  if (!dateString) return '';
  
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
};

export const formatDateSimple = (dateString) => {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleDateString();
};

export const formatDateShort = (date) => {
  if (!date || !(date instanceof Date)) return '';
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

export const formatDateRange = (startDate, endDate, isMobile = false) => {
  if (!startDate || !endDate) return '';
  
  if (isMobile) {
    return `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`;
  }
  
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  
  // If same year, don't repeat it
  if (startDate.getFullYear() === endDate.getFullYear()) {
    const startWithoutYear = startDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric'
    });
    return `${startWithoutYear} - ${end}`;
  }
  
  return `${start} - ${end}`;
};

export const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  return date1.toDateString() === date2.toDateString();
};

export const isDateInRange = (date, startDate, endDate) => {
  if (!date || !startDate || !endDate) return false;
  return date >= startDate && date <= endDate;
};

export const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const isToday = (date) => {
  if (!date) return false;
  return isSameDay(date, new Date());
};

export const isPastDate = (date) => {
  if (!date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate < today;
};

export const getDaysBetween = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const timeDiff = endDate.getTime() - startDate.getTime();
  return Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both dates
};

export const validateDateRange = (startDate, endDate) => {
  const errors = [];
  
  if (!startDate) {
    errors.push('Start date is required');
    return { isValid: false, errors };
  }
  
  if (!endDate) {
    errors.push('End date is required');
    return { isValid: false, errors };
  }
  
  if (isPastDate(startDate)) {
    errors.push('Start date cannot be in the past');
  }
  
  if (endDate <= startDate) {
    errors.push('End date must be after start date');
  }
  
  const daysBetween = getDaysBetween(startDate, endDate);
  
  return {
    isValid: errors.length === 0,
    errors,
    dayCount: daysBetween
  };
};

// Build a YYYY-MM-DD key from LOCAL date components. Using
// `toISOString().split('T')[0]` would shift the date back/forward when local
// midnight straddles UTC midnight (e.g. UK BST, or any negative-UTC timezone).
export const toLocalDateKey = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Compute {startDate, endDate} for a preset mode toggle option. Returns null
// for 'custom' (caller should keep the existing customDateRange).
// `weekStartsOn`: 0 (Sunday) or 1 (Monday).
//   'thisWeek' → tomorrow through end of this calendar week
//   'nextWeek' → first day of next week through end of next week
export const computeRangeForMode = (mode, weekStartsOn = 1) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayDow = today.getDay(); // 0 = Sun, ..., 6 = Sat
  // Last day of the current week is the day before weekStartsOn.
  const lastDayDow = (weekStartsOn + 6) % 7;
  // Days from today to the end of THIS week (0 if today is the last day).
  const daysToEndOfThisWeek = (lastDayDow - todayDow + 7) % 7;

  if (mode === 'thisWeek') {
    const tomorrow = addDays(today, 1);
    const endOfThisWeek = addDays(today, daysToEndOfThisWeek);
    return { startDate: tomorrow, endDate: endOfThisWeek };
  }
  if (mode === 'nextWeek') {
    const startOfNextWeek = addDays(today, daysToEndOfThisWeek + 1);
    return { startDate: startOfNextWeek, endDate: addDays(startOfNextWeek, 6) };
  }
  return null;
};

// Scheduling-flow week range (ISO weeks, Monday → Sunday). The current-week
// start is clamped to tomorrow so already-elapsed days don't appear in
// "This Week" pickers - matching the availability flow's convention.
//
//   offsetWeeks = 0 → tomorrow 00:00 → Sunday 23:59:59.999 of this week
//                     (collapses if today is Sunday; callers already surface
//                      a "no slots" warning in that case)
//   offsetWeeks > 0 → Monday 00:00 → Sunday 23:59:59.999 of that future week
export const getSchedulingWeekRange = (offsetWeeks = 0) => {
  const now = new Date();
  const day = now.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday + offsetWeeks * 7);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  if (offsetWeeks === 0) {
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (tomorrow > monday) {
      return { startDate: tomorrow, endDate: sunday };
    }
  }
  return { startDate: monday, endDate: sunday };
};

// Inclusive day count for a {startDate, endDate} pair. Returns 0 if the
// range is empty (start > end), or if either date is missing.
export const rangeDayCount = (range) => {
  if (!range || !range.startDate || !range.endDate) return 0;
  const ms = new Date(range.endDate).setHours(0, 0, 0, 0) - new Date(range.startDate).setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor(ms / 86400000) + 1);
};

// Build an array of day descriptors for the given customDateRange. Each item
// has `date` (YYYY-MM-DD), `dayName`, `dayNumber`, `month`, `fullDate`,
// `isWeekday`, `isWeekend`. Falls back to "next 7 days starting today" when
// the range is missing.
export const generateDateRange = (customDateRange) => {
  const dates = [];
  let startDate;
  let endDate;

  if (customDateRange?.startDate && customDateRange?.endDate) {
    startDate = new Date(customDateRange.startDate);
    endDate = new Date(customDateRange.endDate);
  } else {
    const today = new Date();
    startDate = new Date(today);
    endDate = new Date(today);
    endDate.setDate(today.getDate() + 6);
  }

  const currentDate = new Date(startDate);
  currentDate.setHours(0, 0, 0, 0);
  const stopDate = new Date(endDate);
  stopDate.setHours(0, 0, 0, 0);

  while (currentDate <= stopDate) {
    dates.push({
      date: toLocalDateKey(currentDate),
      dayName: currentDate.toLocaleDateString('en-GB', { weekday: 'long' }),
      dayNumber: currentDate.getDate(),
      month: currentDate.toLocaleDateString('en-GB', { month: 'short' }),
      fullDate: currentDate.toLocaleDateString('en-GB', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      }),
      isWeekday: currentDate.getDay() >= 1 && currentDate.getDay() <= 5,
      isWeekend: currentDate.getDay() === 0 || currentDate.getDay() === 6,
    });
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};

export const getNext4Weeks = () => {
  const today = new Date();
  const tomorrow = addDays(today, 1);
  return {
    startDate: tomorrow,
    endDate: addDays(tomorrow, 27) // 28 days from tomorrow
  };
};

export const getQuickPresets = () => {
  const today = new Date();
  const tomorrow = addDays(today, 1);
  
  // Get next weekend (Saturday-Sunday)
  const daysUntilSaturday = (6 - today.getDay()) % 7;
  const nextSaturday = addDays(today, daysUntilSaturday || 7);
  const nextSunday = addDays(nextSaturday, 1);
  
  // Get next week (Monday-Sunday)
  const daysUntilMonday = (1 - today.getDay() + 7) % 7;
  const nextMonday = addDays(today, daysUntilMonday || 7);
  const nextWeekEnd = addDays(nextMonday, 6);
  
  return [
    {
      label: 'This Weekend',
      startDate: nextSaturday,
      endDate: nextSunday,
      description: `${formatDateShort(nextSaturday)} - ${formatDateShort(nextSunday)}`
    },
    {
      label: 'Next Week',
      startDate: nextMonday,
      endDate: nextWeekEnd,
      description: `${formatDateShort(nextMonday)} - ${formatDateShort(nextWeekEnd)}`
    },
    {
      label: 'Next 2 Weeks',
      startDate: tomorrow,
      endDate: addDays(tomorrow, 13),
      description: '14 days'
    },
    {
      label: 'Next 4 Weeks',
      startDate: tomorrow,
      endDate: addDays(tomorrow, 27),
      description: '28 days'
    }
  ];
};